/**
 * Advertising service for managing ad campaigns, fetching ads, and tracking analytics
 * Handles all API interactions for the internal advertising system
 */

import { axiosWithoutToken, axiosWithToken } from "@/utils/axios";
import type {
  Advertisement,
  AdPlacementQuery,
  AdImpression,
  AdClick,
  AdCampaign,
  AdCampaignQuery,
  AdCampaignPayload,
  AdvertisementPayload,
  AdAnalyticsResponse,
  AdListResponse,
  CampaignListResponse,
  AdPlacement,
  AdPage,
  PageAdsResponse,
  RejectAdPayload,
  BookedSlotsCalendarResponse,
} from "@/types/advertising";

class AdvertisingServiceClass {
  /**
   * Fetch all ads with optional filtering
   * @param params - Optional query parameters for filtering and pagination
   */
  async GetAllAds(params?: Record<string, unknown>) {
    try {
      const response = await axiosWithToken().get<AdListResponse>(
        "/advertising/admin/ads",
        {
          params,
        },
      );
      return response;
    } catch (error) {
      console.error("Error fetching all ads:", error);
      throw error;
    }
  }

  /**
   * Fetch user ads
   * @param params - Optional query parameters
   */
  async GetMyAds(params?: Record<string, unknown>) {
    try {
      const response = await axiosWithToken().get<AdListResponse>(
        "/advertising/my-ads",
        {
          params,
        },
      );
      return response;
    } catch (error) {
      console.error("Error fetching all ads:", error);
      throw error;
    }
  }

  async GetAdminAds(params?: Record<string, unknown>) {
    try {
      const response = await axiosWithToken().get<AdListResponse>(
        "/advertising/admin/ads",
        {
          params,
        },
      );
      return response;
    } catch (error) {
      console.error("Error fetching all ads:", error);
      throw error;
    }
  }

  /**
   * Fetch ads by placement location
   * @param placement - Ad placement type
   * @param params - Optional query parameters
   */
  async GetAdsByPlacement(
    placement: AdPlacement,
    params?: Omit<AdPlacementQuery, "placement">,
  ) {
    try {
      const queryParams: Record<string, unknown> = { placement };
      if (params) {
        if (params.limit) queryParams.limit = params.limit;
        if (params.position) queryParams.position = params.position;
        if (params.page) queryParams.page = params.page;
        if (params.userId) queryParams.userId = params.userId;
      }
      const response = await axiosWithoutToken.get<AdListResponse>(
        "/advertising/ads",
        {
          params: queryParams,
        },
      );
      return response;
    } catch (error) {
      console.error(`Error fetching ads for placement ${placement}:`, error);
      throw error;
    }
  }

  /**
   * Fetch a single advertisement by ID
   * @param adId - Advertisement ID
   */
  async GetAdById(adId: string) {
    try {
      const response = await axiosWithoutToken.get<Advertisement>(
        `/advertising/ads/${adId}`,
      );
      return response;
    } catch (error) {
      console.error(`Error fetching ad ${adId}:`, error);
      throw error;
    }
  }

  /**
   * Track ad impression
   * @param impressionData - Impression tracking data
   */
  async TrackImpression(
    impressionData: Omit<AdImpression, "id" | "timestamp">,
  ) {
    try {
      const response = await axiosWithoutToken.post(
        "/advertising/track/impression",
        {
          ...impressionData,
          timestamp: new Date().toISOString(),
        },
      );
      return response;
    } catch (error) {
      console.error("Error tracking ad impression:", error);
      // Don't throw to prevent blocking UI
      return null;
    }
  }

  /**
   * Track ad click
   * @param clickData - Click tracking data
   */
  async TrackClick(clickData: Omit<AdClick, "id" | "timestamp">) {
    try {
      const response = await axiosWithoutToken.post(
        "/advertising/track/click",
        {
          ...clickData,
          timestamp: new Date().toISOString(),
        },
      );
      return response;
    } catch (error) {
      console.error("Error tracking ad click:", error);
      // Don't throw to prevent blocking UI
      return null;
    }
  }

  /**
   * Get analytics for a specific ad
   * @param adId - Advertisement ID
   * @param startDate - Analytics start date
   * @param endDate - Analytics end date
   */
  async GetAdAnalytics(adId: string, startDate?: string, endDate?: string) {
    try {
      const response = await axiosWithToken().get<AdAnalyticsResponse>(
        `/advertising/ads/${adId}/analytics`,
        {
          params: {
            startDate,
            endDate,
          },
        },
      );
      return response;
    } catch (error) {
      console.error(`Error fetching analytics for ad ${adId}:`, error);
      throw error;
    }
  }

  /**
   * Fetch all ad campaigns with filtering
   * @param params - Query parameters for filtering and pagination
   */
  async GetCampaigns(params?: AdCampaignQuery) {
    try {
      const response = await axiosWithToken().get<CampaignListResponse>(
        "/advertising/campaigns",
        {
          params,
        },
      );
      return response;
    } catch (error) {
      console.error("Error fetching campaigns:", error);
      throw error;
    }
  }

  /**
   * Fetch user campaigns
   * @param params - Optional query parameters
   */
  async GetMyCampaigns(params?: AdCampaignQuery) {
    try {
      const response = await axiosWithToken().get<CampaignListResponse>(
        "/advertising/my-campaigns",
        {
          params,
        },
      );

      return response;
    } catch (error) {
      console.error("Error fetching user campaigns:", error);
      throw error;
    }
  }

  /**
   * Fetch a single campaign by ID
   * @param campaignId - Campaign ID
   */
  async GetCampaignById(campaignId: string) {
    try {
      const response = await axiosWithToken().get<AdCampaign>(
        `/advertising/campaigns/${campaignId}`,
      );
      return response;
    } catch (error) {
      console.error(`Error fetching campaign ${campaignId}:`, error);
      throw error;
    }
  }

  /**
   * Create a new ad campaign
   * @param campaignData - Campaign creation payload
   */
  async CreateCampaign(campaignData: AdCampaignPayload) {
    try {
      const response = await axiosWithToken().post<AdCampaign>(
        "/advertising/campaigns",
        campaignData,
      );
      return response;
    } catch (error) {
      console.error("Error creating campaign:", error);
      throw error;
    }
  }

  /**
   * Update an existing campaign
   * @param campaignId - Campaign ID
   * @param campaignData - Campaign update payload
   */
  async UpdateCampaign(
    campaignId: string,
    campaignData: Partial<AdCampaignPayload>,
  ) {
    try {
      const response = await axiosWithToken().put<AdCampaign>(
        `/advertising/campaigns/${campaignId}`,
        campaignData,
      );
      return response;
    } catch (error) {
      console.error(`Error updating campaign ${campaignId}:`, error);
      throw error;
    }
  }

  /**
   * Delete a campaign
   * @param campaignId - Campaign ID
   */
  async DeleteCampaign(campaignId: string) {
    try {
      const response = await axiosWithToken().delete(
        `/advertising/campaigns/${campaignId}`,
      );
      return response;
    } catch (error) {
      console.error(`Error deleting campaign ${campaignId}:`, error);
      throw error;
    }
  }

  /**
   * Create a new advertisement
   * @param adData - Advertisement creation payload
   */
  async CreateAdvertisement(adData: AdvertisementPayload) {
    try {
      const response = await axiosWithToken().post<Advertisement>(
        "/advertising/ads",
        adData,
      );
      return response;
    } catch (error) {
      console.error("Error creating advertisement:", error);
      throw error;
    }
  }

  /**
   * Update an existing advertisement
   * @param adId - Advertisement ID
   * @param adData - Advertisement update payload
   */
  async UpdateAdvertisement(
    adId: string,
    adData: Partial<AdvertisementPayload>,
  ) {
    try {
      const response = await axiosWithToken().put<Advertisement>(
        `/advertising/ads/${adId}`,
        adData,
      );
      return response;
    } catch (error) {
      console.error(`Error updating advertisement ${adId}:`, error);
      throw error;
    }
  }

  /**
   * Delete an advertisement
   * @param adId - Advertisement ID
   */
  async DeleteAdvertisement(adId: string) {
    try {
      const response = await axiosWithToken().delete(
        `/advertising/ads/${adId}`,
      );
      return response;
    } catch (error) {
      console.error(`Error deleting advertisement ${adId}:`, error);
      throw error;
    }
  }

  async ApproveCampaign(campaignId: string) {
    try {
      const response = await axiosWithToken().patch<AdCampaign>(
        `/advertising/campaigns/${campaignId}/approve`,
      );

      return response;
    } catch (error) {
      console.error(`Error approving campaign ${campaignId}:`, error);

      throw error;
    }
  }

  async RejectCampaign(campaignId: string, payload: RejectAdPayload) {
    try {
      const response = await axiosWithToken().patch<AdCampaign>(
        `/advertising/campaigns/${campaignId}/reject`,
        payload,
      );

      return response;
    } catch (error) {
      console.error(`Error rejecting campaign ${campaignId}:`, error);

      throw error;
    }
  }

  /**
   * Get campaign analytics with aggregated data
   * @param campaignId - Campaign ID
   * @param startDate - Analytics start date
   * @param endDate - Analytics end date
   */
  async GetCampaignAnalytics(
    campaignId: string,
    startDate?: string,
    endDate?: string,
  ) {
    try {
      const response = await axiosWithToken().get<AdAnalyticsResponse>(
        `/advertising/campaigns/${campaignId}/analytics`,
        {
          params: {
            startDate,
            endDate,
          },
        },
      );
      return response;
    } catch (error) {
      console.error(
        `Error fetching campaign analytics for ${campaignId}:`,
        error,
      );
      throw error;
    }
  }

  async GetPageAds(page: AdPage) {
    try {
      const response = await axiosWithoutToken.get<PageAdsResponse>(
        "/advertising/page-ads",
        {
          params: { page },
        },
      );
      return response;
    } catch (error) {
      console.error(`Error fetching page ads for ${page}:`, error);
      throw error;
    }
  }

  /**
   * Fetches the daily calendar availability mapping for a specific month
   * @param month Format string "YYYY-MM" (e.g., "2026-06")
   */
  async GetBookedSlotsForMonth(month: string) {
    try {
      const response = await axiosWithoutToken.get<BookedSlotsCalendarResponse>(
        "/advertising/booked-slots",
        {
          params: { month },
        },
      );
      return response;
    } catch (error) {
      console.error(`Error fetching booked slots for month ${month}:`, error);
      throw error;
    }
  }

  async ApproveAd(adId: string) {
    try {
      const response = await axiosWithToken().patch<Advertisement>(
        `/advertising/ads/${adId}/approve`,
      );

      return response;
    } catch (error) {
      console.error(`Error approving advertisement ${adId}:`, error);
      throw error;
    }
  }
  async RejectAd(adId: string, payload: RejectAdPayload) {
    try {
      const response = await axiosWithToken().patch<Advertisement>(
        `/advertising/ads/${adId}/reject`,
        payload,
      );

      return response;
    } catch (error) {
      console.error(`Error rejecting advertisement ${adId}:`, error);
      throw error;
    }
  }
}

const AdvertisingService = new AdvertisingServiceClass();
export default AdvertisingService;
